import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAppSelector } from 'src/store';
import {
  useGetProjectByIdQuery,
  useGetZendeskEmailGroupsFieldsQuery,
  useZendeskEmailIntegrationAutoSetupMutation,
} from 'src/services';
import { useForm } from 'react-hook-form';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { ZendeskEscalationGroupSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/ZendeskEscalationGroupSelect/ZendeskEscalationGroupSelect';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const InitialGroupsFields: { id: number | null; name: string }[] = [
  { id: null, name: 'Don’t change group' },
];

export const ZendeskEmailStep4 = ({
  onClick = () => {},
}: {
  onClick?: (integration?: SelectedIntegration) => void;
}) => {
  const { id } = useParams();
  const [groupsFields, setGroupsFields] = useState(InitialGroupsFields);
  const [page, setPage] = useState(1);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const {
    data: groupsFieldsResponse,
    isLoading: isLoadingGroupsFields,
    isFetching: isFetchingGroupsFields,
    isError: isErrorGroupsFields,
  } = useGetZendeskEmailGroupsFieldsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      page: page,
    },
    { skip: !selectedIntegration?.id || !currentProject },
  );
  const [setZendeskEmailAutoSetup, { isLoading: isLoadingSetZendeskEmailAutoSetup }] =
    useZendeskEmailIntegrationAutoSetupMutation();

  const { handleSubmit, control } = useForm<{ escalated_group_id: number | null }>();

  useLoaderWhile(isLoadingGroupsFields, isLoadingSetZendeskEmailAutoSetup);

  useEffect(() => {
    if (groupsFieldsResponse) {
      setGroupsFields([...groupsFields, ...groupsFieldsResponse.items]);

      if (groupsFieldsResponse.has_more) {
        setPage(page + 1);
      }
    }
  }, [groupsFieldsResponse]);

  const submit = async (data: { escalated_group_id: number | null }) => {
    setZendeskEmailAutoSetup({
      projectId: id,
      integrationId: selectedIntegration.id,
      body: {
        escalated_group_id: data.escalated_group_id || '',
      },
    }).then((setZendeskEmailAutoSetupResult) => {
      if (!('error' in setZendeskEmailAutoSetupResult)) {
        onClick({ ...selectedIntegration });
      }
    });
  };

  return (
    <form id="zendesk-email-step4-form" onSubmit={handleSubmit(submit)}>
      <div className="content">
        <LoaderWrapper
          loader={<Skeleton className="w-full h-16" />}
          isLoading={isFetchingGroupsFields}
        >
          {!isErrorGroupsFields ? (
            groupsFields.length > 1 && (
              <div className={'flex flex-col gap-5'}>
                <ZendeskEscalationGroupSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Escalation group{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: <>AI will assign escalated tickets to this group.</>,
                        }}
                      />
                    </div>
                  }
                  control={control}
                  name={'escalated_group_id'}
                  options={groupsFields}
                />
              </div>
            )
          ) : (
            <p>
              The Zendesk details you entered seem invalid. Please double-check your subdomain,
              email, and API token following{' '}
              <a href={selectedIntegration.setupGuideUrl} target="_blank" rel="noreferrer">
                this guide
              </a>
              {'.'}
            </p>
          )}
        </LoaderWrapper>
      </div>
    </form>
  );
};
